import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from 'store';
import { useMessageSocket } from 'hooks/useMessageSocket';
import { usePointsSocket } from 'hooks/usePointsSocket';
import { useImportSocket } from 'hooks/useImportSocket';
import { useImportRecovery } from 'hooks/useImportRecovery';
import { useBadgeSocket } from 'hooks/useBadgeSocket';
import { useOfferSocket } from 'hooks/useOfferSocket';
import {
  PENDING_INCOMING_OFFERS_BADGE_ARGS,
  tradeOffersApi,
} from 'store/services/tradeOffers/tradeOffersApi';
import {
  updateInboxWithNewMessage,
  updateThreadWithNewMessage,
  updateUnreadCount,
  updateInboundTradeCount,
  updateMessageReadStatus,
} from 'store/services/message/messageCacheUtils';
import type {
  NewMessagePayload,
  MessageReadPayload,
  NotificationCountsPayload,
} from 'types/interfaces/message';

interface SocketProviderProps {
  children: React.ReactNode;
}

/**
 * App-level socket provider that initializes WebSocket connection
 * for authenticated users and handles global cache updates.
 *
 * This component should wrap the main app content.
 */
export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = !!user;

  // Handle new message received
  const handleNewMessage = (payload: NewMessagePayload) => {
    console.log('[SocketProvider] New message:', payload);

    // Update inbox cache (moves/creates conversation at top)
    updateInboxWithNewMessage(dispatch, payload);

    // Also update the specific thread if it exists in cache
    // This handles the case where user is viewing another conversation
    updateThreadWithNewMessage(dispatch, payload.conversationId, payload);
  };

  // Handle read receipt received (sender sees recipient read their messages)
  const handleMessageRead = (payload: MessageReadPayload) => {
    console.log('[SocketProvider] Message read:', payload);

    // Update read status in the conversation thread cache
    updateMessageReadStatus(
      dispatch,
      payload.conversationId,
      payload.messageIds,
      payload.readAt
    );
  };

  // Handle notification count updates (badge changes)
  const handleNotificationsUpdate = (payload: NotificationCountsPayload) => {
    console.log('[SocketProvider] Notifications update:', payload);

    // Update unread inbox count
    updateUnreadCount(dispatch, payload.inboxCount);

    // Update inbound trade count
    updateInboundTradeCount(dispatch, payload.receivingCount, payload.hasOverdueTrades);

    // Update the sidebar Offers badge count. This recount also covers offer
    // cancellation, which deliberately emits no offer:update event.
    if (typeof payload.pendingIncomingOffersCount === 'number') {
      const count = payload.pendingIncomingOffersCount;
      dispatch(
        tradeOffersApi.util.updateQueryData(
          'listTradeOffers',
          PENDING_INCOMING_OFFERS_BADGE_ARGS,
          (draft) => {
            draft.totalCount = count;
          }
        )
      );
    }
  };

  // Initialize message socket for authenticated users
  useMessageSocket({
    enabled: isAuthenticated,
    onNewMessage: handleNewMessage,
    onMessageRead: handleMessageRead,
    onNotificationsUpdate: handleNotificationsUpdate,
  });

  // Initialize points socket for real-time balance updates
  usePointsSocket({
    enabled: isAuthenticated,
  });

  // Initialize import socket for real-time import progress
  useImportSocket({
    enabled: isAuthenticated,
  });

  // Initialize badge socket for real-time badge earned/lost events
  useBadgeSocket({
    enabled: isAuthenticated,
  });

  // Initialize offer socket for trade offer lifecycle events (one discriminated
  // offer:update event; toast copy and invalidation switch on its type)
  useOfferSocket({
    enabled: isAuthenticated,
  });

  // Check for active imports on page refresh/app init
  useImportRecovery(isAuthenticated);

  return <>{children}</>;
};

export default SocketProvider;
